import { type Feature, type Polygon } from 'geojson'
import { booleanContains, booleanIntersects } from '@turf/turf'
import {
  type AreaData,
  type AreaId,
  type AreaRunHistoryEntry,
  type UserId,
  getCreatedAt,
  getAreaStateName,
  getNextFutureAreaAssign,
  getNextFutureAreaUnassign,
  isDefined,
  isEmpty,
  isFalse,
  isNotDefined,
  isNotEmpty,
} from '@terros/common'
import { ColorPrimitives } from '../../color'
import {
  type AreaState,
  type TimelineEntry,
  buildUnifiedTimeline,
  getAreaOwners,
} from './state'
import { areaToTurfPolygon, isRenderablePolygonRing } from './geo'

export class Territory {
  private readonly area: AreaData
  constructor(area: AreaData) {
    this.area = area
  }

  get areaId(): AreaId {
    return this.area.areaId
  }

  get data(): AreaData {
    return this.area
  }

  get isAccountAssignment(): boolean {
    return isDefined(this.area.assignmentConfig)
  }

  get assignmentHistory(): AreaRunHistoryEntry[] {
    if (!this.isAccountAssignment) return []
    if (isEmpty(this.area.runHistory)) return []
    return [...this.area.runHistory].sort((a, b) => b.timestamp - a.timestamp)
  }

  get lastRun(): AreaRunHistoryEntry | undefined {
    if (!this.isAccountAssignment) return
    return this.assignmentHistory.at(0)
  }

  get isCurrentAssigned(): boolean {
    if (isFalse(this.isAccountAssignment)) {
      if (isDefined(this.area.teamId)) return true
      if (isNotEmpty(this.area.userIds)) return true
    } else {
      if (isDefined(this.lastRun) && this.lastRun.type !== 'unassign') return true
    }
    return false
  }

  get assignedAt(): number | undefined {
    if (!this.isAccountAssignment) return getCreatedAt(this.area)
    if (isNotDefined(this.lastRun)) return
    if (this.lastRun?.type === 'unassign') return
    return this.lastRun?.timestamp
  }

  get color(): string {
    if (isNotEmpty(this.area.color)) return this.area.color
    return ColorPrimitives.blue['500']
  }

  get hasValidCoordinates(): boolean {
    return isRenderablePolygonRing(this.area.coordinates)
  }

  get areaPolygon(): Feature<Polygon> | undefined {
    if (!this.hasValidCoordinates) return
    return areaToTurfPolygon(this.area)
  }

  get owners(): UserId[] {
    return getAreaOwners(this.area)
  }

  get state(): AreaState {
    const teamId = this.area.teamId
    const userIds = this.owners
    const state = getAreaStateName(this.area)

    const scheduledDate = getNextFutureAreaAssign(this.area)
    const expiryDate = getNextFutureAreaUnassign(this.area)

    if (state === 'draft') return { state }
    if (state === 'scheduled' && isDefined(scheduledDate)) {
      return { state: 'scheduled', teamId, userIds, scheduledDate }
    }

    if (state === 'expiring' && isDefined(expiryDate)) {
      return {
        state: 'expiring',
        teamId,
        userIds,
        lastRunTimestamp: this.lastRun?.timestamp,
        lastRunCount: this.lastRun?.count,
        expiryDate,
      }
    }

    if (state === 'assigned') {
      return {
        state: 'assigned',
        teamId,
        userIds,
        lastRunTimestamp: this.lastRun?.timestamp,
        lastRunCount: this.lastRun?.count,
      }
    }

    return { state: 'unassigned', teamId, userIds }
  }

  get timeline(): TimelineEntry[] {
    return buildUnifiedTimeline(this.area)
  }

  isVisibleInViewport(viewportPolygon: Feature<Polygon>): boolean {
    if (isNotDefined(this.areaPolygon)) return false
    if (!booleanIntersects(this.areaPolygon, viewportPolygon)) return false
    if (booleanContains(this.areaPolygon, viewportPolygon)) return false
    return true
  }
}
